import React from 'react';
import { CatalogDetail } from '../../../apis/detail-catalog';
import styles from '../styles.module.css';
import {
  LocationOnOutlined,
  AccessTimeOutlined,
  ApartmentOutlined,
} from '@mui/icons-material';

interface SectionDetailsProps {
  section: CatalogDetail['departmentSections'][number];
  showMap?: boolean;
}

export const SectionDetails: React.FC<SectionDetailsProps> = ({
  section,
  showMap = true,
}) => {
  const addressParts = [
    section.address,
    section.district,
    `${section.city} - ${section.state}`,
    section.postalCode && `CEP: ${section.postalCode}`,
  ].filter(Boolean);

  const infoItems = [
    {
      icon: LocationOnOutlined,
      text: addressParts.join(' • '),
    },
    section.openingTime && {
      icon: AccessTimeOutlined,
      text: section.openingTime,
    },
    section.department?.shortName && {
      icon: ApartmentOutlined,
      text: section.department.shortName,
    },
  ].filter(Boolean) as Array<{ icon: React.ElementType; text: string }>;

  const fallbackMapQuery =
    section.address && section.city
      ? `${section.address}, ${section.city} - ${section.state || ''}`
      : undefined;

  const fallbackEmbedSrc = fallbackMapQuery
    ? `https://www.google.com/maps?q=${encodeURIComponent(
        fallbackMapQuery
      )}&output=embed`
    : undefined;

  const isGoogleMapsUrl = (url: string) => {
    const normalized = url.toLowerCase();
    return (
      /(^https?:\/\/)?(www\.)?(maps\.)?google\.[^/]+/i.test(normalized) ||
      normalized.includes('goo.gl/maps') ||
      normalized.includes('maps.app.goo.gl')
    );
  };

  const hasEmbedSpecifier = (url: string) =>
    url.includes('/embed') || url.includes('output=embed');

  const mapSrc =
    section.mapUrl &&
    isGoogleMapsUrl(section.mapUrl) &&
    !hasEmbedSpecifier(section.mapUrl) &&
    fallbackEmbedSrc
      ? fallbackEmbedSrc
      : section.mapUrl || fallbackEmbedSrc;

  return (
    <div className={styles.sectionDetails}>
      {showMap && mapSrc && (
        <div className={styles.sectionDetailsMap}>
          <iframe
            title={`Mapa de localização - ${section.name}`}
            src={mapSrc}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
      <div className={styles.sectionDetailsCard}>
        <h3 className={styles.sectionDetailsName}>{section.name}</h3>
        <div className={styles.sectionDetailsInfoList}>
          {infoItems.map((item, index) => (
            <div className={styles.sectionDetailsInfoItem} key={`${item.text}-${index}`}>
              <span className={styles.sectionDetailsInfoIconWrapper}>
                {React.createElement(item.icon, {
                  className: styles.sectionDetailsInfoIcon,
                  fontSize: 'small',
                })}
              </span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
